import { forwardRef, Ref } from 'react';

import {
  InputBase,
  InputLabel,
  InputBaseProps,
  FormHelperText,
  styled,
} from '@mui/material';

import { BaseFieldProps } from '../types';

const InputRoot = styled('div')<{ hideSpinner?: boolean }>(
  ({ hideSpinner }) => ({
    flex: 1,
    width: '100%',
    maxWidth: '100%',
    marginBottom: 8,

    '& .MuiInputBase-root': {
      width: '100%',

      '& input': {
        boxSizing: 'content-box!important',
      },
      '& input:-webkit-autofill': {
        transition: 'background-color 0s 600000s, color 0s 600000s !important',
      },

      '& input:-webkit-autofill:focus': {
        transition: 'background-color 0s 600000s, color 0s 600000s !important',
      },

      // Conditionally hide spin buttons for number inputs
      ...(hideSpinner && {
        '& input[type="number"]': {
          '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0,
          },
          '-moz-appearance': 'textfield', // Firefox
        },
      }),
    },
  }),
);

export interface InputProps
  extends Omit<InputBaseProps, 'value' | 'error'>,
    BaseFieldProps {
  value?: string | number;
  showLabel?: boolean;
  helperText?: string;
  hideSpinner?: boolean;
}

export const Input = forwardRef<Ref<any>, InputProps>((props, ref) => {
  const {
    label,
    error,
    showLabel = true,
    onChange,
    helperText,
    style,
    sx,
    hideSpinner = false,
    ...rest
  } = props;
  return (
    <InputRoot style={style} sx={sx} hideSpinner={hideSpinner}>
      {label && (
        <InputLabel sx={{ visibility: showLabel ? 'visible' : 'hidden' }}>
          {label}
        </InputLabel>
      )}
      <InputBase {...rest} inputRef={ref} onChange={onChange} />
      {error && <FormHelperText error>{error}</FormHelperText>}
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </InputRoot>
  );
});

Input.displayName = 'Input';
